import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeftIcon, MailIcon, PencilIcon, SaveIcon, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import { EmployeeTaxInfoCard } from '@/components/EmployeeTaxInfoCard'
import {
  getEmployeeApi,
  updateEmployeeApi,
  type EmployeeUser,
} from '@/lib/api'

/** Color treatment for each role badge. */
const ROLE_STYLES: Record<string, string> = {
  employee: 'bg-muted text-muted-foreground',
  hr_manager: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  payroll_admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  company_admin: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
}

/** Format an ISO timestamp as a friendly date string. */
function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

export const Route = createFileRoute('/dashboard/employees/$userId')({
  component: () => {
    const { userId } = Route.useParams()
    const [employee, setEmployee] = useState<EmployeeUser | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [editing, setEditing] = useState(false)
    const [photoFailed, setPhotoFailed] = useState(false)

    // Form state for edit mode
    const [editFirstName, setEditFirstName] = useState('')
    const [editLastName, setEditLastName] = useState('')
    const [editRole, setEditRole] = useState('')
    const [editEmail, setEditEmail] = useState('')
    const [editBankName, setEditBankName] = useState('')
    const [editAccountNumber, setEditAccountNumber] = useState('')
    const [editSwiftBic, setEditSwiftBic] = useState('')
    const [editRoutingNumber, setEditRoutingNumber] = useState('')

    useEffect(() => {
      setLoading(true)
      getEmployeeApi(Number(userId))
        .then((res) => {
          setEmployee(res.user)
          setEditFirstName(res.user.first_name)
          setEditLastName(res.user.last_name)
          setEditRole(res.user.role)
          setEditEmail(res.user.email)
          setEditBankName(res.user.bank_name ?? '')
          setEditAccountNumber(res.user.account_number ?? '')
          setEditSwiftBic(res.user.swift_bic ?? '')
          setEditRoutingNumber(res.user.routing_number ?? '')
          setPhotoFailed(false)
        })
        .catch(() => toast.error('Failed to load employee details'))
        .finally(() => setLoading(false))
    }, [userId])

    const handleSave = async () => {
      if (!employee) return
      setSaving(true)
      try {
        const params: {
          first_name?: string; last_name?: string; role?: string; email?: string
          bank_name?: string | null; account_number?: string | null
          swift_bic?: string | null; routing_number?: string | null
        } = {}
        if (editFirstName !== employee.first_name) params.first_name = editFirstName
        if (editLastName !== employee.last_name) params.last_name = editLastName
        if (editRole !== employee.role) params.role = editRole
        if (editEmail !== employee.email) params.email = editEmail
        if (editBankName !== (employee.bank_name ?? '')) params.bank_name = editBankName || null
        if (editAccountNumber !== (employee.account_number ?? '')) params.account_number = editAccountNumber || null
        if (editSwiftBic !== (employee.swift_bic ?? '')) params.swift_bic = editSwiftBic || null
        if (editRoutingNumber !== (employee.routing_number ?? '')) params.routing_number = editRoutingNumber || null

        if (Object.keys(params).length === 0) {
          setEditing(false)
          return
        }

        await updateEmployeeApi(employee.user_id, params)
        toast.success('Employee updated')

        // Refresh the employee data
        const refreshed = await getEmployeeApi(employee.user_id)
        setEmployee(refreshed.user)
        setEditFirstName(refreshed.user.first_name)
        setEditLastName(refreshed.user.last_name)
        setEditRole(refreshed.user.role)
        setEditEmail(refreshed.user.email)
        setEditBankName(refreshed.user.bank_name ?? '')
        setEditAccountNumber(refreshed.user.account_number ?? '')
        setEditSwiftBic(refreshed.user.swift_bic ?? '')
        setEditRoutingNumber(refreshed.user.routing_number ?? '')
        setEditing(false)
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to update employee',
        )
      } finally {
        setSaving(false)
      }
    }

    const handleCancel = () => {
      if (employee) {
        setEditFirstName(employee.first_name)
        setEditLastName(employee.last_name)
        setEditRole(employee.role)
        setEditEmail(employee.email)
        setEditBankName(employee.bank_name ?? '')
        setEditAccountNumber(employee.account_number ?? '')
        setEditSwiftBic(employee.swift_bic ?? '')
        setEditRoutingNumber(employee.routing_number ?? '')
      }
      setEditing(false)
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-2xl">
          <DataListToolbar
            title="Employee details"
            subtitle={
              <span className="inline-block h-4 w-48 animate-pulse rounded bg-muted align-middle" />
            }
          />
          <div className="mt-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        </div>
      )
    }

    if (!employee) {
      return (
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 py-16">
          <p className="text-sm text-muted-foreground">Employee not found.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/dashboard/employees">Back to employees</Link>
          </Button>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        <DataListToolbar
          title="Employee details"
          subtitle={`${employee.first_name} ${employee.last_name}`}
          actions={
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard/employees">
                <ArrowLeftIcon />
                Back
              </Link>
            </Button>
          }
        />

        <div className="mt-8 space-y-6">
          {/* Header card with avatar and name */}
          <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
            <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[var(--brand-mint)]/10 text-xl font-semibold text-[var(--brand-mint)]">
              {employee.profile_photo_url && !photoFailed ? (
                <img
                  src={employee.profile_photo_url}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover"
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                (employee.first_name?.charAt(0) ?? '') +
                  (employee.last_name?.charAt(0) ?? '') || '?'
              )}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold">
                {employee.first_name} {employee.last_name}
              </h2>
              <p className="truncate text-sm text-muted-foreground">
                {employee.pseudonym}
              </p>
              <span className={cn(
                  'mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  ROLE_STYLES[employee.role] ?? 'bg-muted text-muted-foreground',
                )}>
                {employee.role.replace(/_/g, ' ')}
              </span>
            </div>
            {!editing && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto shrink-0 gap-1.5"
                onClick={() => setEditing(true)}
                aria-label="Edit employee"
              >
                <PencilIcon className="size-3.5" />
                Edit
              </Button>
            )}
          </div>

          {/* Details section */}
          <section className="space-y-4 rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold">Account information</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="empFirstName">First name</Label>
                {editing ? (
                  <Input
                    id="empFirstName"
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                  />
                ) : (
                  <p className="text-sm">{employee.first_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="empLastName">Last name</Label>
                {editing ? (
                  <Input
                    id="empLastName"
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                  />
                ) : (
                  <p className="text-sm">{employee.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="empEmail">Email</Label>
              {editing ? (
                <Input
                  id="empEmail"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              ) : (
                <p className="flex items-center gap-1.5 text-sm">
                  <MailIcon className="size-3.5 text-muted-foreground" />
                  {employee.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="empRole">Role</Label>
              {editing ? (
                <Select
                  id="empRole"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                >
                  <option value="employee">Employee</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="payroll_admin">Payroll Admin</option>
                  <option value="company_admin">Company Admin</option>
                </Select>
              ) : (
                <p className="text-sm capitalize">
                  {employee.role.replace(/_/g, ' ')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Joined</Label>
              <p className="text-sm text-muted-foreground">
                {formatDate(employee.created_at)}
              </p>
            </div>

            {employee.employee_identifier && (
              <div className="space-y-2">
                <Label>Employee ID</Label>
                <p className="text-sm text-muted-foreground">
                  {employee.employee_identifier}
                </p>
              </div>
            )}

            {/* ── Bank account details ── */}
            <hr className="border-border" />
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Bank account</h3>
            </div>
            <p className="text-xs text-muted-foreground -mt-3">Used for cross-org payments. All fields are optional.</p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="empBankName">Bank name</Label>
                {editing ? (
                  <Input
                    id="empBankName"
                    type="text"
                    value={editBankName}
                    onChange={(e) => setEditBankName(e.target.value)}
                    placeholder="Not set"
                  />
                ) : (
                  <p className="text-sm">{employee.bank_name || <span className="text-muted-foreground italic">Not set</span>}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="empAccountNumber">Account number</Label>
                {editing ? (
                  <Input
                    id="empAccountNumber"
                    type="text"
                    value={editAccountNumber}
                    onChange={(e) => setEditAccountNumber(e.target.value)}
                    placeholder="Not set"
                  />
                ) : (
                  <p className="text-sm font-mono">{employee.account_number || <span className="text-muted-foreground italic">Not set</span>}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="empSwiftBic">SWIFT / BIC</Label>
                {editing ? (
                  <Input
                    id="empSwiftBic"
                    type="text"
                    value={editSwiftBic}
                    onChange={(e) => setEditSwiftBic(e.target.value)}
                    placeholder="Not set"
                  />
                ) : (
                  <p className="text-sm font-mono">{employee.swift_bic || <span className="text-muted-foreground italic">Not set</span>}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="empRoutingNumber">Routing number</Label>
                {editing ? (
                  <Input
                    id="empRoutingNumber"
                    type="text"
                    value={editRoutingNumber}
                    onChange={(e) => setEditRoutingNumber(e.target.value)}
                    placeholder="Not set"
                  />
                ) : (
                  <p className="text-sm font-mono">{employee.routing_number || <span className="text-muted-foreground italic">Not set</span>}</p>
                )}
              </div>
            </div>

            {/* Edit actions */}
            <div
              className={cn(
                'grid transition-all duration-200 ease-out',
                editing ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <div className="flex items-center gap-3 pt-2">
                  <Button type="button" size="sm" className="gap-1.5" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <SaveIcon className="size-3.5" />
                        Save changes
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <XIcon className="size-3.5" />
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Employer-managed tax information (compliance path — DESIGN_TaxFilingProvision.md §3.2) */}
          <EmployeeTaxInfoCard userId={employee.user_id} />
        </div>
      </div>
    )
  },
})
